import React, { createContext, useContext, useMemo } from 'react';

interface SsoRestrictionContextValue {
  /**
   * Whether the user being edited authenticates via single sign-on. When true,
   * their identity-owned fields (language, email, account type, profile
   * picture, linked buyers/distributors) are read-only because they are managed
   * by the identity provider; name, phone number, and order preferences stay
   * editable.
   */
  isSsoUser: boolean;
}

// Defaults to `false` so components that consume the context outside a provider
// (e.g. the reusable SsoRestrictionTooltip) degrade gracefully to "not
// restricted" rather than throwing.
const SsoRestrictionContext = createContext<SsoRestrictionContextValue>({
  isSsoUser: false,
});

export const SsoRestrictionProvider: React.FC<{
  isSsoUser: boolean;
  children: React.ReactNode;
}> = ({ isSsoUser, children }) => {
  const value = useMemo(() => ({ isSsoUser }), [isSsoUser]);

  return (
    <SsoRestrictionContext.Provider value={value}>
      {children}
    </SsoRestrictionContext.Provider>
  );
};

/**
 * Reads the SSO restriction state for the user currently being edited. Lets
 * form sections lock their identity-owned fields and surface the explanatory
 * tooltip without prop-drilling the flag from `UserForm`.
 */
export const useSsoRestriction = (): SsoRestrictionContextValue =>
  useContext(SsoRestrictionContext);
